import React, { useState } from "react";
import {
  UilCloudUpload,
  UilTrashAlt,
  UilCheckCircle,
} from "@iconscout/react-unicons";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddMovieHeader from "../components/AddMovieHeader";
import PageHeader from "../components/Header";
// import { useAddMovie } from "contexts/AdsMovieContext";
import { uploadFile } from "../components/uploadFile";
import { toast } from "react-toastify";
import Input from "components/Input";
import { FormProvider, useForm } from "react-hook-form";
import useMutation from "hooks/useMutation";

export default function AddTrailer({ onCancel, details }) {
  // const { state, dispatch } = useAddMovie();
  const [trailerFile, setTrailerFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploaded, setUploaded] = useState(false);
  const [controller, setController] = useState(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const hasExistingTrailer = Array.isArray(details?.trailers) && details.trailers.length > 0;
  const { loading: saving, mutate } = useMutation({ method: hasExistingTrailer ? 'put' : 'post', url: `/entertainment/movie-box/medias/${searchParams.get('id')}/trailers` });

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      title: details?.trailers?.[0]?.name || "",
      description: details?.description || "",
    },
  });
  const { handleSubmit, watch } = methods;
  const titleValue = watch('title');

  const handleCancel = () => {
    if (typeof onCancel === "function") onCancel();
    navigate(-1);
  };

  const handleFileChange = (e) => {
    const newFile = e.target.files[0];
    if (!newFile) return;

    setTrailerFile(newFile);
    setUploading(false);
    setProgress(0);
    setUploaded(false);
    setController(null);
  };

  const removeFile = () => {
    if (uploading && controller) controller.abort();
    setTrailerFile(null);
    setProgress(0);
    setUploading(false);
    setUploaded(false);
    setController(null);
  };

  // const goNextStep = () => {
  //   navigate("/movie-box/add-movie/review");
  // };

  const onSubmit = async (values) => {
    if (!trailerFile || uploading) {
      toast.error("Please select a trailer file before continuing.");
      return;
    }

    setUploading(true);
    setProgress(0);
    setUploaded(false);

    const ctrl = new AbortController();
    setController(ctrl);

    try {
      const up = await uploadFile({
        name: values.title || "trailer",
        file: trailerFile,
        mediaId: searchParams.get("id"),
        type: "trailer",
        signal: ctrl.signal,
        onProgress: ({ percent }) => setProgress(percent || 0),
      });

      const key = up?.key;
      if (!key) throw new Error("UPLOAD_KEY_MISSING");

      await mutate({
        payload: {
          title: values.title,
          description: values.description || "",
          path: key,
        },
        onSuccess: () => {
          setUploading(false);
          setUploaded(true);
          setProgress(100);
          toast.success("Trailer saved successfully");
          navigate("/movie-box/add-movie/review");
        },
        onError: (err) => {
          console.error(err);
          setUploading(false);
          setUploaded(false);
          toast.error("Failed to save trailer. Please try again.");
        },
      });
    } catch (error) {
      setUploading(false);
      setUploaded(false);
      console.error(error);
      toast.error("Error uploading trailer. Please try again.");
    } finally {
      setController(null);
    }
  };

  return (
    <FormProvider {...methods}>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <AddMovieHeader
          onCancel={handleCancel}
          showProgress={true}
          currentStep={5}
          continueDisabled={!trailerFile || uploading || saving || !titleValue}
          onContinue={handleSubmit(onSubmit)}
          loading={uploading || saving}
        />

        <main className="flex-1 flex justify-center px-4 py-8">
          <div className="w-full max-w-3xl">
            <div className="flex justify-between items-center">
              <PageHeader
                title="Add trailer"
                description="Upload a short preview to give viewers a taste of your movie. Keep it exciting and engaging."
              />
              {/* <button
                onClick={goNextStep}
                className="ml-4 bg-white text-blue-600 px-4 py-1 rounded shadow hover:bg-gray-50"
              >
                Skip
              </button> */}
            </div>

            {/* Title */}
            <div className="mt-4">
              <Input
                name="title"
                label="Movie title"
                placeholder="Enter movie title"
                required
                var
              />
            </div>

            {/* Description */}
            <div className="mt-4">
              <Input
                name="description"
                label="Movie description"
                placeholder="Describe the movie..."
                multiline
                minRows={4}
              />
            </div>
            <div className="relative mt-10">
              <span className=" left-0 text-sm font-bold text-gray-700">
                Trailer File
              </span>

              <div className="bg-blue-50 border-2 border-dashed rounded-md p-4 flex flex-col justify-center">
                {!trailerFile && (
                  <label className="flex items-center cursor-pointer space-x-3">
                    <UilCloudUpload className="text-blue-500 h-6 w-6" />
                    <div>
                      <p className="text-blue-600 font-medium">
                        Upload your trailer file (MP4, MOV, AVI)
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        MP4, MOV, AVI supported
                      </p>
                    </div>
                    <input
                      type="file"
                      accept=".mp4,.mov,.avi"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </label>
                )}

                {trailerFile && uploading && (
                  <div className="w-full">
                    <p className="text-sm text-gray-800">{trailerFile.name}</p>
                    <div className="flex items-center mt-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500" style={{ width: `${progress}%` }}></div>
                      </div>
                      <span className="ml-3 text-sm text-gray-700">{progress}%</span>
                      <button type="button" onClick={removeFile} className="ml-3 text-gray-500 hover:text-red-600">
                        <UilTrashAlt size={18} />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Uploading...</p>
                  </div>
                )}

                {trailerFile && !uploading && !uploaded && (
                  <div className="w-full flex items-center justify-between">
                    <div className="flex items-start space-x-2">
                      <UilCloudUpload className="text-blue-500 mt-1" size={18} />
                      <div>
                        <p className="text-sm text-gray-800">{trailerFile.name}</p>
                        <p className="text-xs text-gray-500 mt-1">Ready to upload • {(trailerFile.size / (1024 * 1024 * 1024)).toFixed(2)} GB</p>
                      </div>
                    </div>
                    <button type="button" onClick={removeFile} className="text-gray-500 hover:text-red-600">
                      <UilTrashAlt size={18} />
                    </button>
                  </div>
                )}

                {trailerFile && uploaded && (
                  <div className="w-full flex items-center justify-between">
                    <div className="flex items-start space-x-2">
                      <UilCheckCircle className="text-green-600 mt-1" size={18} />
                      <div>
                        <p className="text-sm text-gray-800">{trailerFile.name}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Uploaded • {(trailerFile.size / (1024 * 1024 * 1024)).toFixed(2)} GB
                        </p>
                      </div>
                    </div>
                    <button type="button" onClick={removeFile} className="text-gray-500 hover:text-red-600">
                      <UilTrashAlt size={18} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </FormProvider>
  );
}
